/**
 * Wordmark / Branding Module
 *
 * Supports image logos and styled text wordmarks with decorative accents.
 *
 * Text wordmarks include:
 *   - Font family selection (from loaded Google Fonts)
 *   - Color picker
 *   - Bold / italic / uppercase styling
 *   - 10 decorative accent variations
 */

import * as fabric from 'fabric';

const MIN_WIDTH = 200;
const MIN_HEIGHT = 100;

/** Available fonts (must match Google Fonts link in index.html). */
export const WORDMARK_FONTS = [
  'Bebas Neue',
  'Work Sans',
  'DM Mono',
  'Outfit',
  'Inter',
  'Oswald',
  'Poppins',
  'Raleway',
  'Montserrat',
  'Playfair Display',
  'Space Grotesk',
  'Archivo Black',
  'Rubik',
  'Barlow Condensed',
  'Anton',
  'Righteous',
  'Orbitron',
];

/**
 * Accent decorations rendered as additional Fabric objects
 * grouped with the text wordmark.
 *
 * Each accent function receives (textWidth, textHeight, fontSize, fill)
 * and returns an array of Fabric object configs.
 */
export const WORDMARK_ACCENTS = [
  { id: 'none',        label: 'None' },
  { id: 'underline',   label: 'Underline' },
  { id: 'double-line', label: 'Double Line' },
  { id: 'overline',    label: 'Overline' },
  { id: 'brackets',    label: '[ Brackets ]' },
  { id: 'arrows',      label: '\u25C0 Arrows \u25B6' },
  { id: 'box',         label: 'Box' },
  { id: 'slashes',     label: '// Slashes' },
  { id: 'dots',        label: '\u2022 Dots' },
  { id: 'diamond',     label: '\u25C7 Diamond' },
  { id: 'bar-ends',    label: '| Bar Ends |' },
];

/**
 * Build accent decoration objects for a text wordmark.
 */
function buildAccentObjects(accentId, tw, th, fontSize, fill) {
  const objs = [];
  const stroke = fill;
  const sw = Math.max(1, fontSize * 0.06);
  const gap = fontSize * 0.15;

  switch (accentId) {
    case 'underline':
      objs.push(new fabric.Line([0, th + gap, tw, th + gap], {
        stroke, strokeWidth: sw, selectable: false, evented: false,
      }));
      break;

    case 'double-line':
      objs.push(new fabric.Line([0, th + gap, tw, th + gap], {
        stroke, strokeWidth: sw, selectable: false, evented: false,
      }));
      objs.push(new fabric.Line([0, th + gap + sw * 3, tw, th + gap + sw * 3], {
        stroke, strokeWidth: sw, selectable: false, evented: false,
      }));
      break;

    case 'overline':
      objs.push(new fabric.Line([0, -gap, tw, -gap], {
        stroke, strokeWidth: sw, selectable: false, evented: false,
      }));
      break;

    case 'brackets': {
      const bh = th + gap * 2;
      const bw = fontSize * 0.3;
      // Left bracket
      objs.push(new fabric.Polyline([
        { x: bw, y: -gap },
        { x: 0, y: -gap },
        { x: 0, y: bh },
        { x: bw, y: bh },
      ], { stroke, strokeWidth: sw, fill: 'transparent', selectable: false, evented: false }));
      // Right bracket
      objs.push(new fabric.Polyline([
        { x: tw - bw, y: -gap },
        { x: tw, y: -gap },
        { x: tw, y: bh },
        { x: tw - bw, y: bh },
      ], { stroke, strokeWidth: sw, fill: 'transparent', selectable: false, evented: false }));
      break;
    }

    case 'arrows': {
      const arrowSize = fontSize * 0.35;
      const cy = th / 2;
      // Left arrow
      objs.push(new fabric.Polygon([
        { x: -arrowSize * 1.5, y: cy },
        { x: -arrowSize * 0.5, y: cy - arrowSize * 0.5 },
        { x: -arrowSize * 0.5, y: cy + arrowSize * 0.5 },
      ], { fill: stroke, selectable: false, evented: false }));
      // Right arrow
      objs.push(new fabric.Polygon([
        { x: tw + arrowSize * 1.5, y: cy },
        { x: tw + arrowSize * 0.5, y: cy - arrowSize * 0.5 },
        { x: tw + arrowSize * 0.5, y: cy + arrowSize * 0.5 },
      ], { fill: stroke, selectable: false, evented: false }));
      break;
    }

    case 'box':
      objs.push(new fabric.Rect({
        left: -gap, top: -gap,
        width: tw + gap * 2, height: th + gap * 2,
        stroke, strokeWidth: sw, fill: 'transparent',
        selectable: false, evented: false,
      }));
      break;

    case 'slashes': {
      const slashH = th * 0.7;
      const slashW = fontSize * 0.12;
      const slashGap = fontSize * 0.2;
      const sy = th * 0.15;
      // Left slashes
      for (let i = 0; i < 2; i++) {
        const x0 = -(slashGap * 2) - i * slashGap;
        objs.push(new fabric.Line([x0, sy + slashH, x0 + slashW, sy], {
          stroke, strokeWidth: sw * 1.2, selectable: false, evented: false,
        }));
      }
      // Right slashes
      for (let i = 0; i < 2; i++) {
        const x0 = tw + slashGap + i * slashGap;
        objs.push(new fabric.Line([x0, sy + slashH, x0 + slashW, sy], {
          stroke, strokeWidth: sw * 1.2, selectable: false, evented: false,
        }));
      }
      break;
    }

    case 'dots': {
      const dotR = fontSize * 0.08;
      const cy = th / 2;
      // Left dot
      objs.push(new fabric.Circle({
        left: -fontSize * 0.4, top: cy - dotR,
        radius: dotR, fill: stroke, selectable: false, evented: false,
      }));
      // Right dot
      objs.push(new fabric.Circle({
        left: tw + fontSize * 0.25, top: cy - dotR,
        radius: dotR, fill: stroke, selectable: false, evented: false,
      }));
      break;
    }

    case 'diamond': {
      const ds = fontSize * 0.2;
      const cy = th / 2;
      // Left diamond
      objs.push(new fabric.Polygon([
        { x: -ds * 2.5, y: cy },
        { x: -ds * 1.5, y: cy - ds },
        { x: -ds * 0.5, y: cy },
        { x: -ds * 1.5, y: cy + ds },
      ], { fill: stroke, selectable: false, evented: false }));
      // Right diamond
      objs.push(new fabric.Polygon([
        { x: tw + ds * 0.5, y: cy },
        { x: tw + ds * 1.5, y: cy - ds },
        { x: tw + ds * 2.5, y: cy },
        { x: tw + ds * 1.5, y: cy + ds },
      ], { fill: stroke, selectable: false, evented: false }));
      break;
    }

    case 'bar-ends': {
      const barH = th * 0.8;
      const sy = th * 0.1;
      objs.push(new fabric.Line([-gap * 2, sy, -gap * 2, sy + barH], {
        stroke, strokeWidth: sw * 1.5, selectable: false, evented: false,
      }));
      objs.push(new fabric.Line([tw + gap * 2, sy, tw + gap * 2, sy + barH], {
        stroke, strokeWidth: sw * 1.5, selectable: false, evented: false,
      }));
      break;
    }
  }

  return objs;
}

/**
 * Add a wordmark image (logo) to the Fabric canvas.
 */
export async function addWordmark(fabricCanvas, dataUrl, options = {}) {
  const { displayScale = 1, canvasWidth = 800, canvasHeight = 600, outputWidth = 2550 } = options;

  return new Promise((resolve) => {
    const testImg = new Image();
    testImg.onload = async () => {
      if (testImg.naturalWidth < MIN_WIDTH || testImg.naturalHeight < MIN_HEIGHT) {
        resolve({
          success: false,
          error: `Image too small (${testImg.naturalWidth}\u00d7${testImg.naturalHeight}). Minimum: ${MIN_WIDTH}\u00d7${MIN_HEIGHT}px.`,
        });
        return;
      }

      removeWordmark(fabricCanvas);

      const img = await fabric.FabricImage.fromURL(dataUrl);
      const targetW = canvasWidth * 0.2;
      const scale = targetW / img.width;

      img.set({
        left: canvasWidth * 0.05,
        top: canvasHeight * 0.05,
        scaleX: scale,
        scaleY: scale,
        originX: 'left',
        originY: 'top',
        lockUniScaling: true, // maintain aspect ratio
        data: {
          id: '__wordmark',
          stencilType: 'wordmark',
          originalWidth: testImg.naturalWidth,
          originalHeight: testImg.naturalHeight,
        },
      });

      fabricCanvas.add(img);
      fabricCanvas.renderAll();
      resolve({ success: true, image: img });
    };

    testImg.onerror = () => {
      resolve({ success: false, error: 'Failed to load image. Use PNG, JPG, or SVG.' });
    };
    testImg.src = dataUrl;
  });
}

/**
 * Add a styled text wordmark with optional accent decoration.
 *
 * @param {fabric.Canvas} fabricCanvas
 * @param {string} text
 * @param {object} options
 * @param {number} options.canvasWidth
 * @param {number} options.canvasHeight
 * @param {string} options.fontFamily
 * @param {string} options.fill - text color
 * @param {boolean} options.bold
 * @param {boolean} options.italic
 * @param {boolean} options.uppercase
 * @param {string} options.accent - accent ID from WORDMARK_ACCENTS
 * @returns {fabric.FabricObject} the added object
 */
export function addTextWordmark(fabricCanvas, text, options = {}) {
  const {
    canvasWidth = 800,
    canvasHeight = 600,
    fontFamily = 'Bebas Neue',
    fill = '#ffffff',
    bold = false,
    italic = false,
    uppercase = true,
    accent = 'none',
    preservePosition = false,  // keep existing wordmark position on update
  } = options;

  // Capture existing wordmark position before removing it
  let savedLeft = canvasWidth * 0.05;
  let savedTop = canvasHeight * 0.05;
  if (preservePosition) {
    const existing = fabricCanvas.getObjects().find(o => o.data?.id === '__wordmark');
    if (existing) {
      savedLeft = existing.left;
      savedTop = existing.top;
    }
  }

  removeWordmark(fabricCanvas);

  const displayText = uppercase ? text.toUpperCase() : text;
  const fontSize = Math.max(16, Math.round(canvasWidth * 0.05));

  const textObj = new fabric.Textbox(displayText, {
    left: 0,
    top: 0,
    width: canvasWidth * 0.4,
    fontSize,
    fontFamily,
    fontWeight: bold ? 'bold' : 'normal',
    fontStyle: italic ? 'italic' : 'normal',
    fill,
    originX: 'left',
    originY: 'top',
  });

  // Prevent font size distortion on resize — only change width, keep scale 1:1
  textObj.on('scaling', () => {
    const newWidth = textObj.width * textObj.scaleX;
    textObj.set({ width: Math.max(40, newWidth), scaleX: 1, scaleY: 1 });
  });

  // Measure rendered text dimensions
  const tw = textObj.width;
  const th = textObj.height;

  // Build accent decorations
  const accentObjs = buildAccentObjects(accent, tw, th, fontSize, fill);

  const wordmarkConfig = { text, fontFamily, fill, bold, italic, uppercase, accent };
  let added;

  if (accentObjs.length > 0) {
    // Create a group with text + accents so they move together
    const group = new fabric.Group([textObj, ...accentObjs], {
      left: savedLeft,
      top: savedTop,
      originX: 'left',
      originY: 'top',
      lockUniScaling: true, // keep aspect ratio on resize
      data: {
        id: '__wordmark',
        stencilType: 'wordmark-text',
        wordmarkConfig,
      },
    });

    // NOTE: No scaling lock — allow uniform resizing with accents.
    // The group scales text + accents together proportionally.

    fabricCanvas.add(group);
    added = group;
  } else {
    textObj.set({
      left: savedLeft,
      top: savedTop,
      data: {
        id: '__wordmark',
        stencilType: 'wordmark-text',
        wordmarkConfig,
      },
    });
    fabricCanvas.add(textObj);
    added = textObj;
  }

  fabricCanvas.renderAll();
  return added;
}

/**
 * Remove wordmark from canvas.
 */
export function removeWordmark(fabricCanvas) {
  const wm = fabricCanvas.getObjects().find(o => o.data?.id === '__wordmark');
  if (wm) {
    fabricCanvas.remove(wm);
    fabricCanvas.renderAll();
  }
}

/**
 * Check if a wordmark exists on the canvas.
 */
export function hasWordmark(fabricCanvas) {
  return fabricCanvas.getObjects().some(o => o.data?.id === '__wordmark');
}
